import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import AppLayout from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  AlertTriangle, 
  CheckCircle, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { benchmarkApi, type BenchmarkVersion, type BenchmarkOptions } from "@/lib/api/benchmarks";
import { cn } from "@/lib/utils";

interface StrandFormData {
  description: string;
  subskills: {
    [key: string]: {
      description: string;
      weight: number;
      bands: {
        emerging: { descriptor: string; evidence_rules: string[]; disallowed: string[] };
        expected: { descriptor: string; evidence_rules: string[]; disallowed: string[] };
        exceeding: { descriptor: string; evidence_rules: string[]; disallowed: string[] };
      };
    };
  };
}

interface BenchmarkFormData {
  version: string;
  age_band: string;
  mode: string;
  strands: {
    physical: StrandFormData;
    linguistic: StrandFormData;
    cognitive: StrandFormData;
    social_emotional: StrandFormData;
  };
  scoring_logic: {
    minimum_evidence_clips: number;
    maximum_evidence_clips: number;
    confidence_threshold: number;
    strand_weights: {
      physical: number;
      linguistic: number;
      cognitive: number;
      social_emotional: number;
    };
  };
  feedback_templates: {
    strengths: { [key: string]: string[] };
    next_steps: { [key: string]: string[] };
  };
  notes: string;
}

const STRANDS = [
  { key: "physical", label: "Physical", description: "Physical delivery and voice" },
  { key: "linguistic", label: "Linguistic", description: "Language use and vocabulary" },
  { key: "cognitive", label: "Cognitive", description: "Content organization and thinking" },
  { key: "social_emotional", label: "Social-Emotional", description: "Engagement and presence" },
];

const SUBSKILLS: { [key: string]: { key: string; label: string; description: string }[] } = {
  physical: [
    { key: "voice_projection", label: "Voice Projection", description: "Clear, audible voice" },
    { key: "pace_control", label: "Pace Control", description: "Consistent and appropriate speaking pace" },
    { key: "clarity", label: "Clarity", description: "Clear articulation and pronunciation" },
  ],
  linguistic: [
    { key: "vocabulary_range", label: "Vocabulary Range", description: "Use of varied and appropriate vocabulary" },
    { key: "sentence_structure", label: "Sentence Structure", description: "Varied and effective sentence construction" },
  ],
  cognitive: [
    { key: "structure", label: "Structure", description: "Clear organization of ideas" },
    { key: "content_depth", label: "Content Depth", description: "Depth of content understanding" },
  ],
  social_emotional: [
    { key: "enthusiasm", label: "Enthusiasm", description: "Shows interest and engagement" },
  ],
};

const BANDS = ["emerging", "expected", "exceeding"] as const;
const MODES = ["presenting", "explaining", "persuading"] as const;

export default function AdminBenchmarksPage() {
  const queryClient = useQueryClient();
  const [selectedBenchmark, setSelectedBenchmark] = useState<BenchmarkVersion | null>(null);
  const [editingBenchmark, setEditingBenchmark] = useState<BenchmarkVersion | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [filterMode, setFilterMode] = useState<string>("all");
  const [filterAgeBand, setFilterAgeBand] = useState<string>("all");

  const { data: benchmarks, isLoading } = useQuery({
    queryKey: ["benchmarks", filterMode, filterAgeBand],
    queryFn: () => benchmarkApi.list({
      mode: filterMode !== "all" ? filterMode : undefined,
      age_band: filterAgeBand !== "all" ? filterAgeBand : undefined,
    }),
  });

  const { data: options } = useQuery({
    queryKey: ["benchmarkOptions"],
    queryFn: benchmarkApi.options,
  });

  const activateMutation = useMutation({
    mutationFn: benchmarkApi.activate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
    },
  });

  const deprecateMutation = useMutation({
    mutationFn: ({ id, replacement }: { id: string; replacement?: string }) =>
      benchmarkApi.deprecate(id, replacement),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: benchmarkApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
    },
  });

  const filteredBenchmarks = benchmarks?.results || [];

  return (
    <AppLayout role="admin">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Benchmark Definitions</h1>
            <p className="text-muted-foreground mt-1">
              Manage BDL (Benchmark Definition Language) versions
            </p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) setEditingBenchmark(null); }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Create Benchmark
              </Button>
            </DialogTrigger>
            <CreateBenchmarkDialog 
              options={options}
              editingBenchmark={editingBenchmark}
              onClose={() => { setIsCreateOpen(false); setEditingBenchmark(null); }}
              onSuccess={() => {
                setIsCreateOpen(false);
                setEditingBenchmark(null);
                queryClient.invalidateQueries({ queryKey: ["benchmarks"] });
              }}
            />
          </Dialog>
        </div>

        <div className="flex gap-4">
          <Select value={filterMode} onValueChange={setFilterMode}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Modes</SelectItem>
              {options?.modes.map(mode => (
                <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterAgeBand} onValueChange={setFilterAgeBand}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by age band" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Age Bands</SelectItem>
              {options?.age_bands.map(age => (
                <SelectItem key={age} value={age}>{age}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Benchmark Versions</CardTitle>
            <CardDescription>
              {filteredBenchmarks.length} benchmark(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : filteredBenchmarks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No benchmarks found. Create one to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Age Band</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBenchmarks.map(benchmark => (
                    <TableRow key={benchmark.id}>
                      <TableCell className="font-medium">{benchmark.version}</TableCell>
                      <TableCell>{benchmark.age_band}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{benchmark.mode}</Badge>
                      </TableCell>
                      <TableCell>
                        {benchmark.isDeprecated ? (
                          <Badge variant="destructive">Deprecated</Badge>
                        ) : benchmark.is_active ? (
                          <Badge className="bg-green-500">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(benchmark.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedBenchmark(benchmark);
                              setIsViewOpen(true);
                            }}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setEditingBenchmark(benchmark);
                              setIsCreateOpen(true);
                            }}
                            title="Edit Benchmark"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!benchmark.isDeprecated && !benchmark.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => activateMutation.mutate(benchmark.id)}
                              disabled={activateMutation.isPending}
                              title="Activate"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {!benchmark.isDeprecated && benchmark.is_active && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deprecateMutation.mutate({ id: benchmark.id })}
                              disabled={deprecateMutation.isPending}
                              title="Deprecate"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              if (confirm("Are you sure you want to delete this benchmark?")) {
                                deleteMutation.mutate(benchmark.id);
                              }
                            }}
                            disabled={deleteMutation.isPending}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Benchmark: {selectedBenchmark?.version} - {selectedBenchmark?.age_band} - {selectedBenchmark?.mode}
              </DialogTitle>
            </DialogHeader>
            {selectedBenchmark && (
              <BenchmarkDefinitionView definition={selectedBenchmark.definition} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

function BenchmarkDefinitionView({ definition }: { definition: BenchmarkVersion["definition"] }) {
  const strands = definition?.strands || {};
  const scoring = definition?.scoring_logic || {};
  const feedback = definition?.feedback_templates || {};

  return (
    <div className="space-y-6">
      <Tabs defaultValue="strands">
        <TabsList>
          <TabsTrigger value="strands">Strands</TabsTrigger>
          <TabsTrigger value="scoring">Scoring Logic</TabsTrigger>
          <TabsTrigger value="feedback">Feedback Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="strands" className="space-y-4">
          {Object.entries(strands).map(([strandName, strand]) => (
            <Card key={strandName}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg capitalize">{strandName}</CardTitle>
                <CardDescription>{strand?.description || ""}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(strand?.subskills || {}).map(([subskillName, subskill]) => (
                    <div key={subskillName} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{subskillName}</span>
                        <Badge variant="outline">Weight: {subskill?.weight ?? 0}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{subskill?.description || ""}</p>
                      
                      <div className="grid grid-cols-3 gap-4">
                        {Object.entries(subskill?.bands || {}).map(([band, bandDef]: [string, unknown]) => (
                          <div key={band} className="text-sm">
                            <div className="font-medium capitalize mb-1">{band}</div>
                            <p className="text-xs text-muted-foreground mb-2">{bandDef?.descriptor || ""}</p>
                            {(bandDef?.evidence_rules || []).length > 0 && (
                              <div className="text-xs">
                                <span className="font-medium">Rules: </span>
                                {(bandDef.evidence_rules || []).map((rule: string, i: number) => (
                                  <span key={i} className="bg-muted px-1 rounded mr-1">{rule}</span>
                                ))}
                              </div>
                            )}
                            {(bandDef?.disallowed || []).length > 0 && (
                              <div className="text-xs mt-1">
                                <span className="font-medium">Disallowed: </span>
                                {(bandDef.disallowed || []).map((d: string, i: number) => (
                                  <span key={i} className="text-red-500 mr-1">{d}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="scoring">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Evidence Clips</Label>
                  <div className="text-2xl font-bold">{scoring.minimum_evidence_clips ?? 0}</div>
                </div>
                <div>
                  <Label>Max Evidence Clips</Label>
                  <div className="text-2xl font-bold">{scoring.maximum_evidence_clips ?? 0}</div>
                </div>
                <div>
                  <Label>Confidence Threshold</Label>
                  <div className="text-2xl font-bold">{scoring.confidence_threshold ?? 0}</div>
                </div>
              </div>
              <div className="mt-4">
                <Label>Strand Weights</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {Object.entries(scoring.strand_weights || {}).map(([strand, weight]) => (
                    <div key={strand} className="bg-muted rounded p-2 text-center">
                      <div className="text-sm capitalize">{strand}</div>
                      <div className="font-bold">{weight ?? 0}</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Strengths</CardTitle>
              </CardHeader>
              <CardContent>
                {feedback.strengths && Object.entries(feedback.strengths).map(([mode, items]: [string, unknown]) => (
                  <div key={mode} className="mb-4">
                    <div className="font-medium capitalize mb-2">{mode}</div>
                    <ul className="text-sm space-y-1">
                      {Array.isArray(items) && items.map((item: string, i: number) => (
                        <li key={i} className="text-muted-foreground">• {item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                {feedback.next_steps && Object.entries(feedback.next_steps).map(([mode, items]: [string, unknown]) => (
                  <div key={mode} className="mb-4">
                    <div className="font-medium capitalize mb-2">{mode}</div>
                    <ul className="text-sm space-y-1">
                      {Array.isArray(items) && items.map((item: string, i: number) => (
                        <li key={i} className="text-muted-foreground">• {item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function getDefaultFormData(): BenchmarkFormData {
  return {
    version: "v1.0.0",
    age_band: "Band 1",
    mode: "presenting",
    strands: {
      physical: { description: "Physical delivery and voice", subskills: {} },
      linguistic: { description: "Language use and vocabulary", subskills: {} },
      cognitive: { description: "Content organization and thinking", subskills: {} },
      social_emotional: { description: "Engagement and presence", subskills: {} },
    },
    scoring_logic: {
      minimum_evidence_clips: 3,
      maximum_evidence_clips: 8,
      confidence_threshold: 0.6,
      strand_weights: { physical: 0.25, linguistic: 0.25, cognitive: 0.35, social_emotional: 0.15 },
    },
    feedback_templates: {
      strengths: { presenting: [], explaining: [], persuading: [] },
      next_steps: { presenting: [], explaining: [], persuading: [] },
    },
    notes: "",
  };
}

function CreateBenchmarkDialog({ 
  options, 
  editingBenchmark,
  onClose, 
  onSuccess 
}: { 
  options: BenchmarkOptions | undefined;
  editingBenchmark?: BenchmarkVersion | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<BenchmarkFormData>(() => {
    if (editingBenchmark?.definition) {
      return {
        version: editingBenchmark.version,
        age_band: editingBenchmark.age_band,
        mode: editingBenchmark.mode,
        strands: editingBenchmark.definition.strands || getDefaultFormData().strands,
        scoring_logic: editingBenchmark.definition.scoring_logic || getDefaultFormData().scoring_logic,
        feedback_templates: editingBenchmark.definition.feedback_templates || getDefaultFormData().feedback_templates,
        notes: editingBenchmark.notes || "",
      };
    }
    return getDefaultFormData();
  });

  // The initializer only runs once, so editing a different benchmark would keep
  // the stale prefill — reset formData whenever the row being edited changes.
  useEffect(() => {
    if (editingBenchmark?.definition) {
      setFormData({
        version: editingBenchmark.version,
        age_band: editingBenchmark.age_band,
        mode: editingBenchmark.mode,
        strands: editingBenchmark.definition.strands || getDefaultFormData().strands,
        scoring_logic: editingBenchmark.definition.scoring_logic || getDefaultFormData().scoring_logic,
        feedback_templates: editingBenchmark.definition.feedback_templates || getDefaultFormData().feedback_templates,
        notes: editingBenchmark.notes || "",
      });
    } else {
      setFormData(getDefaultFormData());
    }
  }, [editingBenchmark]);

  const [activeTab, setActiveTab] = useState("basic");
  const [expandedStrands, setExpandedStrands] = useState<Set<string>>(new Set(["physical"]));
  const [expandedSubskills, setExpandedSubskills] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const toggleStrand = (strand: string) => {
    const newSet = new Set(expandedStrands);
    if (newSet.has(strand)) newSet.delete(strand);
    else newSet.add(strand);
    setExpandedStrands(newSet);
  };

  const toggleSubskill = (key: string) => {
    const newSet = new Set(expandedSubskills);
    if (newSet.has(key)) newSet.delete(key);
    else newSet.add(key);
    setExpandedSubskills(newSet);
  };

  const updateStrandDescription = (strand: string, desc: string) => {
    setFormData(prev => ({
      ...prev,
      strands: { ...prev.strands, [strand]: { ...prev.strands[strand as keyof typeof prev.strands], description: desc } }
    }));
  };

  const updateSubskill = (strand: string, subskillKey: string, field: string, value: unknown) => {
    setFormData(prev => {
      const newStrands = JSON.parse(JSON.stringify(prev.strands));
      const strandData = newStrands[strand];
      const subskills = strandData.subskills;
      
      if (!subskills[subskillKey]) {
        subskills[subskillKey] = {
          description: SUBSKILLS[strand]?.find(s => s.key === subskillKey)?.description || "",
          weight: 0.25,
          bands: {
            emerging: { descriptor: "", evidence_rules: [], disallowed: [] },
            expected: { descriptor: "", evidence_rules: [], disallowed: [] },
            exceeding: { descriptor: "", evidence_rules: [], disallowed: [] },
          }
        };
      }
      
      if (field.startsWith("bands.")) {
        const parts = field.split(".");
        const band = parts[1];
        const bandField = parts[2];
        subskills[subskillKey].bands[band][bandField] = value;
      } else {
        subskills[subskillKey][field] = value;
      }
      
      return { ...prev, strands: newStrands };
    });
  };

  const updateScoring = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      scoring_logic: { ...prev.scoring_logic, [field]: value }
    }));
  };

  const updateWeight = (strand: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      scoring_logic: {
        ...prev.scoring_logic,
        strand_weights: { ...prev.scoring_logic.strand_weights, [strand]: value }
      }
    }));
  };

  const updateFeedback = (type: string, mode: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      feedback_templates: {
        ...prev.feedback_templates,
        [type]: { ...prev.feedback_templates[type as keyof typeof prev.feedback_templates], [mode]: value.split("\n").filter(Boolean) }
      }
    }));
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const definition = {
        version: formData.version,
        age_band: formData.age_band,
        mode: formData.mode,
        strands: formData.strands,
        scoring_logic: formData.scoring_logic,
        feedback_templates: formData.feedback_templates,
      };
      return benchmarkApi.create({ version: formData.version, age_band: formData.age_band, mode: formData.mode, definition, notes: formData.notes });
    },
    onSuccess,
    onError: (err: unknown) => setError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Failed to create benchmark"),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const definition = {
        version: formData.version,
        age_band: formData.age_band,
        mode: formData.mode,
        strands: formData.strands,
        scoring_logic: formData.scoring_logic,
        feedback_templates: formData.feedback_templates,
      };
      return benchmarkApi.update(editingBenchmark!.id, { version: formData.version, age_band: formData.age_band, mode: formData.mode, definition, notes: formData.notes });
    },
    onSuccess,
    onError: (err: unknown) => setError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || "Failed to update benchmark"),
  });

  const handleSubmit = () => {
    if (!formData.version || !formData.age_band || !formData.mode) {
      setError("Please fill in all required fields");
      return;
    }
    if (editingBenchmark) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isEditing = !!editingBenchmark;
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit Benchmark" : "Create New Benchmark"}</DialogTitle>
      </DialogHeader>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="strands">Strands & Subskills</TabsTrigger>
          <TabsTrigger value="scoring">Scoring</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        <div className="mt-4 overflow-y-auto max-h-[60vh] pr-2">
          {activeTab === "basic" && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Version</Label>
                  <Input value={formData.version} onChange={e => setFormData({ ...formData, version: e.target.value })} placeholder="v1.0.0" />
                </div>
                <div>
                  <Label>Age Band</Label>
                  <Select value={formData.age_band} onValueChange={val => setFormData({ ...formData, age_band: val })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {options?.age_bands.map(age => <SelectItem key={age} value={age}>{age}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={formData.mode} onValueChange={val => setFormData({ ...formData, mode: val })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {options?.modes.map(mode => <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Release notes..." />
              </div>
            </div>
          )}

          {activeTab === "strands" && (
            <div className="space-y-3">
              {STRANDS.map(strand => (
                <div key={strand.key} className="border rounded-lg">
                  <div className="flex items-center justify-between p-3 bg-muted/50 cursor-pointer" onClick={() => toggleStrand(strand.key)}>
                    <div className="flex items-center gap-2">
                      {expandedStrands.has(strand.key) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <span className="font-medium capitalize">{strand.label}</span>
                    </div>
                    <Badge variant="outline">{Object.keys(formData.strands[strand.key as keyof typeof formData.strands].subskills).length} subskills</Badge>
                  </div>
                  {expandedStrands.has(strand.key) && (
                    <div className="p-3 space-y-3 border-t">
                      <div>
                        <Label className="text-sm">Strand Description</Label>
                        <Input value={formData.strands[strand.key as keyof typeof formData.strands].description} 
                          onChange={e => updateStrandDescription(strand.key, e.target.value)} placeholder={strand.description} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Subskills</Label>
                        {SUBSKILLS[strand.key]?.map(subskill => (
                          <div key={subskill.key} className="border rounded-md">
                            <div className="flex items-center justify-between p-2 bg-muted/30 cursor-pointer" onClick={() => toggleSubskill(subskill.key)}>
                              <div className="flex items-center gap-2">
                                {expandedSubskills.has(subskill.key) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                <span className="font-medium">{subskill.label}</span>
                              </div>
                            </div>
                            {expandedSubskills.has(subskill.key) && (
                              <div className="p-3 space-y-3 border-t">
                                <div>
                                  <Label className="text-xs">Description</Label>
                                  <Input value={formData.strands[strand.key as keyof typeof formData.strands].subskills[subskill.key]?.description || ""}
                                    onChange={e => updateSubskill(strand.key, subskill.key, "description", e.target.value)} placeholder={subskill.description} />
                                </div>
                                <div>
                                  <Label className="text-xs">Weight (0-1)</Label>
                                  <Input type="number" step="0.05" min="0" max="1" 
                                    value={formData.strands[strand.key as keyof typeof formData.strands].subskills[subskill.key]?.weight || 0.25}
                                    onChange={e => updateSubskill(strand.key, subskill.key, "weight", parseFloat(e.target.value))} />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                  {BANDS.map(band => (
                                    <div key={band} className="space-y-1">
                                      <Label className="text-xs capitalize">{band} Descriptor</Label>
                                      <Textarea value={formData.strands[strand.key as keyof typeof formData.strands].subskills[subskill.key]?.bands[band]?.descriptor || ""}
                                        onChange={e => updateSubskill(strand.key, subskill.key, `bands.${band}.descriptor`, e.target.value)}
                                        placeholder={`${band} level description`} className="text-xs h-16" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === "scoring" && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Min Evidence Clips</Label>
                  <Input type="number" min="1" value={formData.scoring_logic.minimum_evidence_clips}
                    onChange={e => updateScoring("minimum_evidence_clips", parseInt(e.target.value))} />
                </div>
                <div>
                  <Label>Max Evidence Clips</Label>
                  <Input type="number" min="1" value={formData.scoring_logic.maximum_evidence_clips}
                    onChange={e => updateScoring("maximum_evidence_clips", parseInt(e.target.value))} />
                </div>
                <div>
                  <Label>Confidence Threshold (0-1)</Label>
                  <Input type="number" step="0.05" min="0" max="1" value={formData.scoring_logic.confidence_threshold}
                    onChange={e => updateScoring("confidence_threshold", parseFloat(e.target.value))} />
                </div>
              </div>
              <div>
                <Label>Strand Weights (must sum to 1.0)</Label>
                <div className="grid grid-cols-4 gap-3 mt-2">
                  {STRANDS.map(strand => (
                    <div key={strand.key}>
                      <Label className="text-sm capitalize">{strand.label}</Label>
                      <Input type="number" step="0.05" min="0" max="1" value={formData.scoring_logic.strand_weights[strand.key as keyof typeof formData.scoring_logic.strand_weights]}
                        onChange={e => updateWeight(strand.key, parseFloat(e.target.value))} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "feedback" && (
            <div className="space-y-4">
              {MODES.map(mode => (
                <Card key={mode}>
                  <CardHeader className="py-3">
                    <CardTitle className="text-base capitalize">{mode}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm">Strengths (one per line)</Label>
                      <Textarea value={formData.feedback_templates.strengths[mode]?.join("\n") || ""}
                        onChange={e => updateFeedback("strengths", mode, e.target.value)} 
                        placeholder="Enter each strength on a new line" className="h-24" />
                    </div>
                    <div>
                      <Label className="text-sm">Next Steps (one per line)</Label>
                      <Textarea value={formData.feedback_templates.next_steps[mode]?.join("\n") || ""}
                        onChange={e => updateFeedback("next_steps", mode, e.target.value)} 
                        placeholder="Enter each next step on a new line" className="h-24" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Tabs>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={isPending}>
          {isPending ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update Benchmark" : "Create Benchmark")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}